import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Stamp } from "lucide-react";
import AppShell from "@/components/platform/AppShell";
import { ApprovalBadge, EmptyState, PageTitle } from "@/components/platform/Badges";
import { ApprovalPanel } from "@/components/platform/ApprovalPanel";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtDT } from "@/lib/platform";

const SCOPES = [
  ["mine_pending", "Awaiting my decision"],
  ["requested", "Requested by me"],
  ["all", "All approvals"],
];

export default function Approvals() {
  const auth = useAuth();
  const [params, setParams] = useSearchParams();
  const [scope, setScope] = useState("mine_pending");
  const [data, setData] = useState({ approvals: [], pending_for_me: 0 });
  const [loading, setLoading] = useState(true);
  const selected = params.get("approval");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/platform/approvals", { params: { scope } });
      setData(d);
      if (!selected && d.approvals[0]) setParams({ approval: d.approvals[0].id }, { replace: true });
    } finally {
      setLoading(false);
    }
  }, [scope, selected, setParams]);

  useEffect(() => { load(); }, [load]);

  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    api.get(`/platform/approvals/${selected}`).then(({ data: d }) => setDetail(d)).catch(() => setDetail(null));
  }, [selected]);

  return (
    <AppShell testId="approvals-page">
      <PageTitle eyebrow="Approval engine" title="Approvals"
                 subtitle="Request, approve, reject and discuss decisions across every module, with the full history kept." />
      <div className="flex gap-2 flex-wrap">
        {SCOPES.map(([k, l]) => (
          <button key={k} onClick={() => { setScope(k); setParams({}); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                    scope === k ? "bg-[#003366] text-white border-[#003366]" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                  data-testid={`approval-scope-${k}`}>
            {l}{k === "mine_pending" && data.pending_for_me > 0 && (
              <span className="ml-1.5 px-1.5 rounded-full bg-red-600 text-white text-[10px]" data-testid="approval-pending-count">
                {data.pending_for_me}
              </span>
            )}
          </button>
        ))}
      </div>
      {!loading && data.approvals.length === 0 ? (
        <EmptyState icon={Stamp} title="Nothing here" hint="Approvals you can act on or have requested will appear here." testId="approvals-empty" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 items-start">
          <ul className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden" data-testid="approval-list">
            {data.approvals.map((a) => (
              <li key={a.id}>
                <button onClick={() => setParams({ approval: a.id })} data-testid={`approval-item-${a.id}`}
                        className={`w-full text-left px-4 py-3 transition-colors ${selected === a.id ? "bg-blue-50/60" : "hover:bg-slate-50"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-mono text-slate-500">{a.approval_ref}</span>
                    <ApprovalBadge status={a.status} />
                  </div>
                  <div className="text-sm font-medium text-[#003366] truncate mt-0.5">{a.title}</div>
                  <div className="text-[11px] text-slate-500">{a.requested_by} · {fmtDT(a.requested_at)}</div>
                </button>
              </li>
            ))}
          </ul>
          {detail ? (
            <ApprovalPanel approval={detail} currentUser={auth.user?.username}
                           onChanged={(d) => { setDetail({ ...detail, ...d }); load(); }} />
          ) : (
            <div className="text-sm text-slate-500 p-6">Select an approval to review.</div>
          )}
        </div>
      )}
    </AppShell>
  );
}
